// Server-sent events over fetch.
//
// EventSource cannot do what the cabinet needs: it only issues GETs, so it can
// neither post a body nor send the X-Auth-Token header every call authenticates
// with. So the stream is read off a plain fetch, and this file is the part of
// EventSource that is left once the transport is our own — the text/event-stream
// parser — plus the envelope handling RequestAbstract.load does for JSON calls.
//
// load() cannot be reused: it always awaits response.json(), which would sit on
// an open stream until the run ended and then fail to parse it.

const parseData = (raw) => {
    try {
        return JSON.parse(raw);
    } catch {
        // Every event the cabinet sends is one line of JSON. Anything else is
        // handed over as the text it is rather than dropped.
        return raw;
    }
};

/**
 * A text/event-stream parser. Feed it decoded text in whatever pieces the
 * network delivers — an event, a line or a CRLF pair may be split anywhere — and
 * it calls `onEvent(type, data)` once per complete event, `data` being the
 * JSON-parsed payload (or the raw text when it is not JSON).
 *
 * Follows the EventSource parsing rules: lines end in CRLF, LF or CR; a blank
 * line dispatches; `event:` names the type (default "message"); repeated `data:`
 * lines are joined with "\n"; one space after the colon is dropped; lines
 * starting with ":" are comments (the server's `: ping` keep-alives); `id:` and
 * `retry:` are ignored, since they only drive EventSource's reconnection and a
 * POST stream is never resumed.
 */
export function createEventStreamParser(onEvent) {
    let buffer = '';
    let type = '';
    let dataLines = [];

    const dispatch = () => {
        // An event with no data lines is not dispatched, as in EventSource.
        if (dataLines.length) {
            onEvent(type || 'message', parseData(dataLines.join('\n')));
        }
        type = '';
        dataLines = [];
    };

    const processLine = (line) => {
        if (line === '') {
            dispatch();
            return;
        }
        if (line[0] === ':') return;

        const colon = line.indexOf(':');
        const field = colon === -1 ? line : line.slice(0, colon);
        let value = colon === -1 ? '' : line.slice(colon + 1);
        if (value[0] === ' ') value = value.slice(1);

        if (field === 'event') type = value;
        else if (field === 'data') dataLines.push(value);
    };

    return {
        push(text) {
            let chunk = buffer + text;
            // A trailing CR may be the first half of a CRLF whose LF is in the
            // next chunk. Read as a line end now, the LF would then end a second,
            // empty line — a blank line, which dispatches an event early.
            let held = '';
            if (chunk.endsWith('\r')) {
                held = '\r';
                chunk = chunk.slice(0, -1);
            }
            const lines = chunk.split(/\r\n|\r|\n/);
            buffer = lines.pop() + held;
            lines.forEach(processLine);
        },

        /**
         * The stream has ended. A CR held back by push() did end its line after
         * all — and if that line was blank, it dispatches. Anything else still
         * pending is an event that never got its blank line, and is dropped as
         * EventSource drops it: a cut connection can leave half a `data:` line
         * behind, and acting on a truncated `done` would be worse than reporting
         * that the stream ended early.
         */
        end() {
            if (buffer.endsWith('\r')) processLine(buffer.slice(0, -1));
            buffer = '';
            type = '';
            dataLines = [];
        }
    };
}

const isAbort = (error) => error?.name === 'AbortError';

/**
 * POSTs `body` as JSON and reads the answer as an event stream.
 *
 * Resolves — never rejects — to the cabinet's envelope shape, so callers branch
 * on `hasError()` and show `formatApiErrors()` as they do for any other call:
 *
 * - the stream ran to its end: `{ status: 200, error: 'OK' }`
 * - `signal` was aborted, before or during the stream: `{ error: 'OK', aborted: true }`.
 *   Aborting is how a caller stops the stream, so it is not an error.
 * - refused before streaming (any non-2xx, or a 2xx that is not an event
 *   stream): the server's JSON envelope — `{ status: 422, error: 'API_ERROR',
 *   message: 'XSCRAPER_UNREACHABLE: …' }`, or a 400's `data` list of
 *   "field: message" lines — with `error` and `message` always set
 * - the network failed, or the stream broke off: `{ error: 'NETWORK_ERROR' |
 *   'STREAM_FAILED', message }`
 *
 * `onEvent(type, data)` is called for every event as it arrives. An exception
 * thrown from it is logged and the stream carries on: a rendering bug in one
 * event's handler should not cut off the run's later events.
 */
export async function postEventStream(url, { headers = {}, body, signal, onEvent }) {
    const resp = { code: 'initial', status: 0 };

    const deliver = (type, data) => {
        try {
            onEvent?.(type, data);
        } catch (error) {
            console.error(`Event stream handler failed on "${type}":`, error);
        }
    };

    let response;
    try {
        response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
                ...headers
            },
            body: JSON.stringify(body),
            signal
        });
    } catch (error) {
        if (isAbort(error)) return { ...resp, error: 'OK', aborted: true };
        return { ...resp, error: 'NETWORK_ERROR', message: error.message };
    }
    resp.status = response.status;

    const contentType = response.headers.get('Content-Type') || '';
    if (!response.ok || !contentType.includes('text/event-stream')) {
        let envelope = {};
        try {
            envelope = JSON.parse(await response.text());
        } catch (error) {
            if (isAbort(error)) return { ...resp, error: 'OK', aborted: true };
            // Not JSON — a proxy's error page, say. The status is the useful part.
        }
        return {
            ...resp,
            ...envelope,
            error:
                envelope.error && envelope.error !== 'OK'
                    ? envelope.error
                    : response.ok
                        ? 'UNEXPECTED_RESPONSE'
                        : 'HTTP_ERROR',
            message: envelope.message || `HTTP error! status: ${response.status}`
        };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    const parser = createEventStreamParser(deliver);

    try {
        let chunk = await reader.read();
        while (!chunk.done) {
            // stream: true keeps a multi-byte character split across two chunks
            // from decoding as two replacement characters.
            parser.push(decoder.decode(chunk.value, { stream: true }));
            chunk = await reader.read();
        }
        parser.push(decoder.decode());
        parser.end();
        return { ...resp, error: 'OK' };
    } catch (error) {
        if (isAbort(error) || signal?.aborted) return { ...resp, error: 'OK', aborted: true };
        return { ...resp, error: 'STREAM_FAILED', message: error.message };
    }
}
